import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  THEME_LIST,
  applyThemeCssVars,
  clearThemeCssVars,
  getThemeBaseType,
  getLinkedPalette,
} from '../theme/themes';
import { getActiveSeason } from '../theme/seasonalThemes';

const STORAGE_KEY = 'jdm-theme';
// Season key (e.g. 'trickOrTreat-2026') the user opted out of by picking a theme
const SEASON_DISMISSED_KEY = 'jdm-season-dismissed';
const DARK = 'dark';
const LIGHT = 'light';

function getPreferredTheme() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    // Legacy values 'dark' / 'light' are valid theme IDs
    return stored;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? DARK
    : LIGHT;
}

function resolveInitialTheme() {
  const season = getActiveSeason();
  if (season && localStorage.getItem(SEASON_DISMISSED_KEY) !== season.seasonKey) {
    return { themeName: season.themeId, seasonKey: season.seasonKey };
  }
  return { themeName: getPreferredTheme(), seasonKey: null };
}

const ThemeContext = createContext(null);

/**
 * Wrap your app with <ThemeProvider> once, then call useTheme() anywhere
 * in the tree to get the shared theme state.
 *
 * Returned context shape:
 *   { isDark, themeName, toggleTheme, setTheme, themeList, linkedPaletteId }
 *
 * - `isDark` — boolean, true when the active base type is 'dark'
 * - `themeName` — string ID of the active theme (e.g. 'light', 'dark', 'oldGlory')
 * - `toggleTheme()` — shortcut: flips between 'light' and 'dark'
 * - `setTheme(id)` — switch to any theme by ID
 * - `themeList` — array of { id, displayName } for enabled themes
 * - `linkedPaletteId` — palette ID locked by the current theme, or null
 */
export function ThemeProvider({ children }) {
  const [initial] = useState(resolveInitialTheme);
  const [themeName, setThemeName] = useState(initial.themeName);
  // Non-null while a seasonal theme was applied automatically; it must not be
  // saved as the user's preference, so their own theme returns after the season.
  const autoSeasonKeyRef = useRef(initial.seasonKey);

  const isDark = getThemeBaseType(themeName) === DARK;
  const linkedPaletteId = getLinkedPalette(themeName);

  // Keep <html data-theme="..."> in sync, apply CSS vars, and persist.
  useEffect(() => {
    const baseType = getThemeBaseType(themeName);
    document.documentElement.setAttribute('data-theme', baseType);

    // Always clear previous theme's CSS var overrides first, then apply
    // the new theme's overrides (if any). This prevents stale vars from a
    // previous custom theme leaking into a different theme.
    clearThemeCssVars();
    if (themeName !== LIGHT && themeName !== DARK) {
      applyThemeCssVars(themeName);
    }

    if (!autoSeasonKeyRef.current) {
      localStorage.setItem(STORAGE_KEY, themeName);
    }
  }, [themeName]);

  // Any explicit pick (including the seasonal theme itself) becomes the saved
  // preference and dismisses only the season active at pick time (re-evaluated
  // so sessions that cross a season boundary dismiss the correct one).
  const chooseTheme = useCallback((id) => {
    const season = getActiveSeason();
    if (season) {
      localStorage.setItem(SEASON_DISMISSED_KEY, season.seasonKey);
    }
    autoSeasonKeyRef.current = null;
    localStorage.setItem(STORAGE_KEY, id);
    setThemeName(id);
  }, []);

  const setTheme = chooseTheme;

  const toggleTheme = useCallback(() => {
    // Simple toggle always goes between the two base themes
    chooseTheme(getThemeBaseType(themeName) === DARK ? LIGHT : DARK);
  }, [themeName, chooseTheme]);

  return (
    <ThemeContext.Provider
      value={{ isDark, themeName, toggleTheme, setTheme, themeList: THEME_LIST, linkedPaletteId }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Consume the shared theme state.
 * Must be used inside <ThemeProvider>.
 *
 * Returns: { isDark, themeName, toggleTheme, setTheme, themeList, linkedPaletteId }
 */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
